import { useHasPermission } from '@shared/permissions';
import { isInternalEmailDomain } from '@shared/utils';
import { useConfig } from '@user-management/configuration';
import { APP_PERMISSIONS } from '@user-management/permissions';
import { useEffect, useMemo, useRef } from 'react';
import { type UseFormReturn } from 'react-hook-form';

import { UserStatus } from '../../constants';
import { type User, type UserDetailsDisabledFields, type UserDetailsFormValues } from '../../types';

function mapAppRolesForm(roles: string[]) {
  return roles.map((role) => {
    return { value: role, label: role };
  });
}

function mapStatusFormOption(status?: string) {
  if (status === UserStatus.ENABLED) {
    return 'true';
  } else if (status === UserStatus.DISABLED) {
    return 'false';
  }
  return undefined;
}

export default function useUserDetailsFormLogic(
  form: UseFormReturn<UserDetailsFormValues>,
  user: User | undefined,
  isOwner: boolean,
) {
  const { reset } = form;
  const hasReset = useRef(false);

  const hasEditAppRolesPermission = useHasPermission([
    APP_PERMISSIONS.SETTING.USER.UPDATE,
    APP_PERMISSIONS.SETTING.ROLE.UPDATE,
  ]);
  const hasEditStatusPermission = useHasPermission(APP_PERMISSIONS.SETTING.USER.STATUS);
  const hasEditUserPermission = useHasPermission(APP_PERMISSIONS.SETTING.USER.UPDATE);

  const configuration = useConfig();

  const restrictedUserEmailDomains = configuration.userManagement.restrictedUserEmailDomains;

  const isRestrictedEmailDomain = user?.email
    ? isInternalEmailDomain(user?.email, restrictedUserEmailDomains)
    : false;

  const disabledFields: UserDetailsDisabledFields = useMemo(() => {
    const isInInvitingProcess =
      user?.status &&
      [UserStatus.INVITATION_PENDING, UserStatus.INVITATION_EXPIRED].includes(user?.status);

    return {
      appRoles: isOwner || !hasEditAppRolesPermission,
      activated: isOwner || !hasEditStatusPermission || isInInvitingProcess,
      firstName: !hasEditUserPermission || isRestrictedEmailDomain,
      lastName: !hasEditUserPermission || isRestrictedEmailDomain,
      email: true,
    };
  }, [
    hasEditAppRolesPermission,
    hasEditStatusPermission,
    hasEditUserPermission,
    isOwner,
    user?.status,
    isRestrictedEmailDomain,
  ]);

  useEffect(() => {
    // NOTE: Form only first time
    if (!user || hasReset.current) {
      return;
    }
    reset(
      {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        activated: mapStatusFormOption(user?.status),
        appRoles: mapAppRolesForm(user?.appRoles ?? []),
      },
      { keepDefaultValues: true },
    );
    hasReset.current = true;
  }, [user, reset]);

  return { disabledFields };
}
